import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import SignaturePad from 'signature_pad';
import { useToast } from '../context/ToastProvider';

const API = process.env.REACT_APP_API_URL || 'https://app.chile-turismo.cl';

const CLAUSULA = `Yo, el/la abajofirmante, confirmo mi asistencia al tour indicado y declaro conocer y aceptar las siguientes condiciones:

1. ASISTENCIA: Me comprometo a estar presente en el punto de encuentro en la fecha y hora indicada.
2. CANCELACIÓN: Entiendo que la cancelación con menos de 48 horas de anticipación no tendrá reembolso.
3. RESPONSABILIDAD: Chile Turismo no se hace responsable por objetos de valor perdidos durante el tour.
4. SALUD: Declaro estar en condiciones físicas adecuadas para realizar la actividad.
5. FOTOGRAFÍAS: Autorizo el uso de fotografías tomadas durante el tour con fines promocionales.

Al firmar este documento, acepto todas las condiciones anteriores.`;

export default function ConfirmarAsistencia() {
  const { token } = useParams();
  const toast = useToast();
  const [paso, setPaso] = useState(1);
  const [datos, setDatos] = useState(null);
  const [error, setError] = useState('');
  const [clausulaOk, setClausulaOk] = useState(false);
  const [cargando, setCargando] = useState(true);
  const [enviando, setEnviando] = useState(false);
  const [exito, setExito] = useState(false);
  const canvasRef = useRef(null);
  const sigPadRef = useRef(null);
  const [reduceMotion, setReduceMotion] = useState(
    () => typeof window !== 'undefined'
      && typeof window.matchMedia === 'function'
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );

  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return;
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const onChange = () => setReduceMotion(mq.matches);
    mq.addEventListener ? mq.addEventListener('change', onChange) : mq.addListener(onChange);
    return () => {
      mq.removeEventListener ? mq.removeEventListener('change', onChange) : mq.removeListener(onChange);
    };
  }, []);

  useEffect(() => {
    fetch(`${API}/checkin/${token}`)
      .then(r => r.json())
      .then(d => {
        if (d.detail) { setError(d.detail); }
        else { setDatos(d); }
        setCargando(false);
      })
      .catch(() => { setError('Error de conexión'); setCargando(false); });
  }, [token]);

  useEffect(() => {
    if (paso === 3 && canvasRef.current) {
      sigPadRef.current = new SignaturePad(canvasRef.current, {
        backgroundColor: 'rgb(255,255,255)',
        penColor: '#1a3c6e',
      });
      const resize = () => {
        const canvas = canvasRef.current;
        const pad = sigPadRef.current;
        if (!canvas || !pad) return;
        // Preservar el trazo ya dibujado ANTES de redimensionar: cambiar
        // canvas.width/height vacía el bitmap, así que guardamos los puntos.
        const data = pad.toData();
        const ratio = window.devicePixelRatio || 1;
        canvas.width = canvas.offsetWidth * ratio;
        canvas.height = canvas.offsetHeight * ratio;
        canvas.getContext('2d').scale(ratio, ratio);
        // fromData() limpia y re-dibuja el trazo en el nuevo sistema de
        // coordenadas (los puntos van en px CSS, no dependen del ratio),
        // por lo que la firma se conserva tras rotar / abrir el teclado.
        pad.fromData(data);
      };
      resize();
      window.addEventListener('resize', resize);
      window.addEventListener('orientationchange', resize);
      return () => {
        window.removeEventListener('resize', resize);
        window.removeEventListener('orientationchange', resize);
        sigPadRef.current && sigPadRef.current.off && sigPadRef.current.off();
      };
    }
  }, [paso]);

  const limpiarFirma = () => sigPadRef.current?.clear();

  const enviarFirma = async () => {
    if (!sigPadRef.current || sigPadRef.current.isEmpty()) {
      toast.error('Por favor, dibuja tu firma antes de continuar.');
      return;
    }
    setEnviando(true);
    const firma_png = sigPadRef.current.toDataURL('image/png');
    try {
      const r = await fetch(`${API}/checkin/${token}/firma`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ firma_png, clausula_ok: true }),
      });
      const d = await r.json();
      if (d.ok) { setExito(true); }
      else { toast.error(d.detail || 'Error al guardar la firma'); }
    } catch { toast.error('Error de conexión'); }
    finally { setEnviando(false); }
  };

  if (cargando) return (
    <div style={st.centro} role="status" aria-live="polite">
      <style>{'@keyframes spin{to{transform:rotate(360deg)}}'}</style>
      <div
        aria-hidden="true"
        style={{ ...st.spinner, animation: reduceMotion ? 'none' : 'spin 1s linear infinite' }}
      ></div>
      <p>Cargando tu reserva…</p>
    </div>
  );
  if (error)   return <div style={st.centro} role="alert"><h2 style={{color:'#c0392b'}}><span aria-hidden="true">❌</span> {error}</h2></div>;
  if (exito)   return (
    <div style={st.centro}>
      <div style={{fontSize:64}} aria-hidden="true">✅</div>
      <h2 style={{color:'#1a6b3a'}}>¡Asistencia Confirmada!</h2>
      <p>Gracias <strong>{datos?.buyer_name}</strong>. Tu firma ha sido registrada.</p>
      <p style={{color:'#666'}}>Nos vemos en el tour. ¡Buen viaje!</p>
    </div>
  );
  if (datos?.estado === 'confirmado') return (
    <div style={st.centro}>
      <div style={{fontSize:64}} aria-hidden="true">✅</div>
      <h2>Ya confirmaste tu asistencia</h2>
      <p>Reserva <strong>{datos.numero_reserva}</strong> — firma registrada el {datos.timestamp_firma?.split('T')[0]}.</p>
    </div>
  );

  return (
    <div style={st.contenedor}>
      {/* Progress bar */}
      <div style={st.progress} role="list" aria-label={`Progreso: paso ${paso} de 3`}>
        {[1,2,3].map(n => (
          <div
            key={n}
            role="listitem"
            aria-current={paso === n ? 'step' : undefined}
            aria-label={`Paso ${n} de 3: ${['Resumen','Cláusula','Firma'][n-1]}${paso === n ? ' (actual)' : ''}`}
            style={{...st.paso, ...(paso >= n ? st.pasoActivo : {})}}
          >
            <div style={{...st.circulo, ...(paso >= n ? st.circuloActivo : {})}} aria-hidden="true">{n}</div>
            <span style={{fontSize:12}} aria-hidden="true">{['Resumen','Cláusula','Firma'][n-1]}</span>
          </div>
        ))}
      </div>

      {/* PASO 1 — Resumen */}
      {paso === 1 && (
        <div style={st.card}>
          <h2 style={st.titulo}><span aria-hidden="true">📋</span> Tu Reserva</h2>
          <table style={st.tabla}>
            <tbody>
              <tr><td style={st.td1}>Nº Reserva</td><td style={st.td2}><strong>{datos.numero_reserva}</strong></td></tr>
              <tr><td style={st.td1}>Pasajero</td><td style={st.td2}>{datos.buyer_name}</td></tr>
              <tr><td style={st.td1}>Email</td><td style={st.td2}>{datos.buyer_email}</td></tr>
              <tr><td style={st.td1}>Paquete</td><td style={st.td2}>{datos.package_title}</td></tr>
              <tr><td style={st.td1}>Fecha</td><td style={st.td2}>{datos.departure_date}</td></tr>
              <tr><td style={st.td1}>Pasajeros</td><td style={st.td2}>{datos.quantity}</td></tr>
              <tr><td style={st.td1}>Monto pagado</td><td style={st.td2}><strong>${Number(datos.amount_clp).toLocaleString('es-CL')} CLP</strong></td></tr>
            </tbody>
          </table>
          <button style={st.btnVerde} onClick={() => setPaso(2)}>Continuar →</button>
        </div>
      )}

      {/* PASO 2 — Cláusula */}
      {paso === 2 && (
        <div style={st.card}>
          <h2 style={st.titulo}><span aria-hidden="true">📄</span> Cláusula de Participación</h2>
          <div style={st.clausulaBox}>{CLAUSULA}</div>
          <label style={st.checkLabel}>
            <input type="checkbox" checked={clausulaOk} onChange={e => setClausulaOk(e.target.checked)}
                   style={{marginRight:8, width:18, height:18}} />
            He leído y acepto todas las condiciones
          </label>
          <div style={{display:'flex', gap:12, marginTop:20}}>
            <button style={st.btnGris} onClick={() => setPaso(1)}>← Volver</button>
            <button style={{...st.btnVerde, opacity: clausulaOk ? 1 : 0.5}}
                    disabled={!clausulaOk} onClick={() => setPaso(3)}>
              Continuar →
            </button>
          </div>
        </div>
      )}

      {/* PASO 3 — Firma */}
      {paso === 3 && (
        <div style={st.card}>
          <h2 style={st.titulo}><span aria-hidden="true">✍️</span> Firma Digital</h2>
          <p id="firma-instruccion" style={{color:'#555',marginBottom:12}}>
            <span aria-hidden="true">📱</span> En celular: dibuja con el dedo &nbsp;|&nbsp; <span aria-hidden="true">💻</span> En computador: dibuja con el mouse
          </p>
          <div style={st.canvasWrapper}>
            <canvas
              ref={canvasRef}
              style={st.canvas}
              aria-label="Área de firma: dibuja tu firma con el dedo o el mouse"
              aria-describedby="firma-instruccion"
            />
          </div>
          <div style={{display:'flex', gap:12, marginTop:16, flexWrap:'wrap'}}>
            <button style={st.btnGris} onClick={() => setPaso(2)}>← Volver</button>
            <button style={st.btnGris} onClick={limpiarFirma}><span aria-hidden="true">🗑</span> Limpiar</button>
            <button style={{...st.btnVerde, flex:1}} onClick={enviarFirma} disabled={enviando}>
              {enviando ? 'Guardando…' : <><span aria-hidden="true">✅</span> Confirmar Asistencia</>}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

const st = {
  contenedor: { maxWidth:600, margin:'40px auto', padding:'0 16px', fontFamily:'Arial,sans-serif' },
  card: { background:'#fff', borderRadius:12, padding:28, boxShadow:'0 2px 16px rgba(0,0,0,0.1)' },
  titulo: { color:'#1a3c6e', marginTop:0, marginBottom:20 },
  tabla: { width:'100%', borderCollapse:'collapse', marginBottom:20 },
  td1: { padding:'10px 8px', background:'#f5f7fa', fontWeight:'bold', width:'40%', fontSize:14 },
  td2: { padding:'10px 8px', fontSize:14 },
  clausulaBox: { background:'#f9f9f9', border:'1px solid #ddd', borderRadius:8, padding:16,
                 whiteSpace:'pre-wrap', fontSize:13, lineHeight:1.6, maxHeight:300, overflowY:'auto', marginBottom:16 },
  checkLabel: { display:'flex', alignItems:'center', fontSize:15, cursor:'pointer', fontWeight:'bold' },
  canvasWrapper: { border:'2px solid #1a3c6e', borderRadius:8, background:'#fff',
                   width:'100%', aspectRatio:'3/1', minHeight:160, position:'relative',
                   overscrollBehavior:'contain', WebkitTapHighlightColor:'transparent' },
  canvas: { width:'100%', height:'100%', display:'block', touchAction:'none',
            overscrollBehavior:'contain', WebkitTapHighlightColor:'transparent' },
  btnVerde: { background:'#1a6b3a', color:'#fff', border:'none', borderRadius:8,
              padding:'12px 24px', fontSize:16, cursor:'pointer', fontWeight:'bold' },
  btnGris: { background:'#6c757d', color:'#fff', border:'none', borderRadius:8,
             padding:'12px 20px', fontSize:15, cursor:'pointer' },
  progress: { display:'flex', justifyContent:'center', gap:32, marginBottom:28 },
  paso: { display:'flex', flexDirection:'column', alignItems:'center', gap:6, color:'#aaa' },
  pasoActivo: { color:'#1a3c6e', fontWeight:'bold' },
  circulo: { width:32, height:32, borderRadius:'50%', background:'#ddd',
             display:'flex', alignItems:'center', justifyContent:'center', fontSize:14, fontWeight:'bold' },
  circuloActivo: { background:'#1a3c6e', color:'#fff' },
  centro: { maxWidth:500, margin:'80px auto', textAlign:'center', fontFamily:'Arial,sans-serif', padding:24 },
  spinner: { width:40, height:40, border:'4px solid #f3f3f3', borderTop:'4px solid #1a3c6e',
             borderRadius:'50%', animation:'spin 1s linear infinite', margin:'0 auto 20px' },
};
